import {SliceStatus} from 'interfaces';

import {ActionReducerMapBuilder, createSlice} from '@reduxjs/toolkit';

import {
  ExercisesPaginationType,
  ExerciseType,
  GeneratedExerciseData,
} from './utils/constants';
import {FormSteps} from './CreateExercise';
import {
  generateExercise,
  generateExerciseFailure,
  generateExerciseSuccess,
  getExercises,
  getExercisesFailure,
  getExercisesSuccess,
  publishExercise,
  publishExerciseFailure,
  publishExerciseSuccess,
  regenerateExerciseQuestion,
  regenerateExerciseQuestionFailure,
  regenerateExerciseQuestionSuccess,
  resetExercises,
  resetWellniteExercise,
  saveExerciseDraft,
  saveSuperCoachGeneratedExercise,
  saveSuperCoachGeneratedExerciseFailure,
  saveSuperCoachGeneratedExerciseSuccess,
  sendInitialExerciseRequest,
  sendInitialExerciseRequestFailure,
  sendInitialExerciseRequestSuccess,
  updateExerciseAnswers,
  updateExerciseAnswersFailure,
  updateExerciseAnswersSuccess,
} from './wellniteExerciseActions';

export type WellniteExerciseSliceState = {
  status: SliceStatus;
  formStep?: FormSteps;
  exercise: ExerciseType;
  generatedExercise: GeneratedExerciseData;
  questions?: string[];
  regenerateExerciseStatus?: SliceStatus;
  error: string;
  exercises: {
    exercises: ExerciseType[];
    pagination: ExercisesPaginationType;
  };
  exercisesStatus: SliceStatus;
  saveSuperCoachGeneratedExercisesStatus?: SliceStatus;
};

export const wellniteExerciseSliceInitialState: WellniteExerciseSliceState = {
  status: SliceStatus.idle,
  exercise: {} as ExerciseType,
  generatedExercise: {} as GeneratedExerciseData,
  regenerateExerciseStatus: SliceStatus.idle,
  error: '',
  exercises: {
    exercises: [],
    pagination: {
      currentPage: 1,
      hasNextPage: true,
      totalExercises: 0,
      nextPage: 1,
    },
  },
  exercisesStatus: SliceStatus.idle,
  saveSuperCoachGeneratedExercisesStatus: SliceStatus.idle,
};

const wellniteExerciseSlice = createSlice({
  name: 'wellniteExercise',
  initialState: wellniteExerciseSliceInitialState,
  reducers: {},
  extraReducers: (
    builder: ActionReducerMapBuilder<WellniteExerciseSliceState>,
  ) =>
    builder
      .addCase(generateExercise, state => {
        state.status = SliceStatus.pending;
        state.error = '';
      })
      .addCase(generateExerciseFailure, (state, action) => {
        state.status = SliceStatus.rejected;
        state.error = action.payload;
      })
      .addCase(generateExerciseSuccess, (state, {payload}) => {
        state.generatedExercise = payload;
        state.status = SliceStatus.resolved;
        state.error = '';
      })
      .addCase(regenerateExerciseQuestion, state => {
        state.status = SliceStatus.pending;
        state.regenerateExerciseStatus = SliceStatus.pending;
        state.error = '';
      })
      .addCase(regenerateExerciseQuestionFailure, (state, action) => {
        state.status = SliceStatus.rejected;
        state.regenerateExerciseStatus = SliceStatus.rejected;
        state.error = action.payload;
      })
      .addCase(regenerateExerciseQuestionSuccess, (state, {payload}) => {
        state.questions = payload.questions;
        state.regenerateExerciseStatus = SliceStatus.resolved;
        state.status = SliceStatus.resolved;
        state.error = '';
      })
      .addCase(saveExerciseDraft, (state, action) => {
        Object.assign(state.generatedExercise, action.payload);
        state.status = SliceStatus.resolved;
        state.error = '';
      })
      .addCase(publishExercise, state => {
        state.status = SliceStatus.pending;
        state.error = '';
      })
      .addCase(publishExerciseFailure, (state, action) => {
        state.status = SliceStatus.rejected;
        state.error = action.payload;
      })
      .addCase(publishExerciseSuccess, (state, {payload}) => {
        state.exercise = payload;
        state.exercises.exercises.unshift(payload); // Add to beginning of array
        state.formStep = FormSteps.generateExerciseTemplate;
        state.status = SliceStatus.resolved;
        state.error = '';
      })
      .addCase(resetWellniteExercise, state => {
        state.generatedExercise = {} as GeneratedExerciseData;
        state.status = SliceStatus.idle;
        state.error = '';
      })
      .addCase(updateExerciseAnswers, state => {
        state.status = SliceStatus.pending;
        state.error = '';
      })
      .addCase(updateExerciseAnswersFailure, (state, {payload}) => {
        state.status = SliceStatus.rejected;
        state.error = payload;
      })
      .addCase(updateExerciseAnswersSuccess, (state, {payload}) => {
        Object.assign(state.exercise, payload);

        // Update the exercise in the exercises array
        for (let i = 0; i < state.exercises.exercises.length; i++) {
          if (state.exercises.exercises[i]._id === payload._id) {
            Object.assign(state.exercises.exercises[i], payload);
          }
        }

        state.status = SliceStatus.resolved;
        state.error = '';
      })
      .addCase(sendInitialExerciseRequest, state => {
        state.status = SliceStatus.pending;
        state.error = '';
      })
      .addCase(sendInitialExerciseRequestFailure, (state, {payload}) => {
        state.status = SliceStatus.rejected;
        state.error = payload;
      })
      .addCase(sendInitialExerciseRequestSuccess, state => {
        state.status = SliceStatus.resolved;
        state.error = '';
      })
      .addCase(saveSuperCoachGeneratedExercise, (state, {payload}) => {
        state.saveSuperCoachGeneratedExercisesStatus = SliceStatus.pending;
        // Update exercises with new status
        for (let i = 0; i < state.exercises.exercises.length; i++) {
          if (state.exercises.exercises[i]._id === payload.id) {
            state.exercises.exercises[i].status = 'new';
          }
        }
        state.error = '';
      })
      .addCase(saveSuperCoachGeneratedExerciseFailure, (state, {payload}) => {
        state.saveSuperCoachGeneratedExercisesStatus = SliceStatus.rejected;
        state.error = payload;
      })
      .addCase(saveSuperCoachGeneratedExerciseSuccess, state => {
        state.saveSuperCoachGeneratedExercisesStatus = SliceStatus.resolved;
        state.error = '';
      })
      .addCase(getExercises, state => {
        state.exercisesStatus = SliceStatus.pending;
      })
      .addCase(getExercisesFailure, state => {
        state.exercisesStatus = SliceStatus.rejected;
      })
      .addCase(getExercisesSuccess, (state, action) => {
        // Append new exercises to the existing ones using forEach for better Immer performance
        action.payload.exercises.forEach(exercise => {
          state.exercises.exercises.push(exercise);
        });

        // Direct property assignment for pagination properties
        state.exercises.pagination = action.payload.pagination;
        state.exercisesStatus = SliceStatus.resolved;
      })
      .addCase(resetExercises, state => {
        state.exercisesStatus = SliceStatus.idle;
        state.exercises.exercises = [];
        state.exercises.pagination = {
          currentPage: 1,
          hasNextPage: true,
          totalExercises: 0,
          nextPage: 1,
        };
      }),
});

export const {
  reducer: wellniteExerciseReducer,
  name: wellniteExerciseReducerName,
} = wellniteExerciseSlice;

export type TWellniteExerciseActions =
  | ReturnType<typeof generateExercise>
  | ReturnType<typeof generateExerciseFailure>
  | ReturnType<typeof generateExerciseSuccess>
  | ReturnType<typeof regenerateExerciseQuestion>
  | ReturnType<typeof regenerateExerciseQuestionFailure>
  | ReturnType<typeof regenerateExerciseQuestionSuccess>
  | ReturnType<typeof saveExerciseDraft>
  | ReturnType<typeof publishExercise>
  | ReturnType<typeof publishExerciseFailure>
  | ReturnType<typeof publishExerciseSuccess>
  | ReturnType<typeof resetWellniteExercise>
  | ReturnType<typeof getExercises>
  | ReturnType<typeof getExercisesFailure>
  | ReturnType<typeof getExercisesSuccess>
  | ReturnType<typeof resetExercises>
  | ReturnType<typeof updateExerciseAnswers>
  | ReturnType<typeof updateExerciseAnswersFailure>
  | ReturnType<typeof updateExerciseAnswersSuccess>
  | ReturnType<typeof sendInitialExerciseRequest>
  | ReturnType<typeof sendInitialExerciseRequestFailure>
  | ReturnType<typeof sendInitialExerciseRequestSuccess>
  | ReturnType<typeof saveSuperCoachGeneratedExercise>
  | ReturnType<typeof saveSuperCoachGeneratedExerciseFailure>
  | ReturnType<typeof saveSuperCoachGeneratedExerciseSuccess>;

export const wellniteExerciseActions = {
  generateExercise,
  generateExerciseFailure,
  generateExerciseSuccess,
  regenerateExerciseQuestion,
  regenerateExerciseQuestionFailure,
  regenerateExerciseQuestionSuccess,
  saveExerciseDraft,
  publishExercise,
  publishExerciseFailure,
  publishExerciseSuccess,
  resetWellniteExercise,
  getExercises,
  getExercisesFailure,
  getExercisesSuccess,
  resetExercises,
  updateExerciseAnswers,
  updateExerciseAnswersFailure,
  updateExerciseAnswersSuccess,
  sendInitialExerciseRequest,
  sendInitialExerciseRequestFailure,
  sendInitialExerciseRequestSuccess,
  saveSuperCoachGeneratedExercise,
  saveSuperCoachGeneratedExerciseFailure,
  saveSuperCoachGeneratedExerciseSuccess,
};

export type WellniteExerciseState = ReturnType<typeof wellniteExerciseReducer>;
